import { type FormEvent, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  AspectKind,
  AspectLifetime,
  AspectScope,
  AspectVisibility,
} from "../lib/character-edits.js";

export interface AspectFormValues {
  text: string;
  kind: AspectKind;
  visibility: AspectVisibility;
  lifetime: AspectLifetime;
  scope: AspectScope;
}

export interface AspectFormModalProps {
  open: boolean;
  mode: "create" | "edit";
  initial?: Partial<AspectFormValues>;
  allowedKinds?: readonly AspectKind[];
  onSubmit: (v: AspectFormValues) => void;
  onCancel: () => void;
}

const DEFAULT_KINDS: AspectKind[] = ["character", "situation", "boost", "scene", "zone", "setting"];
const LIFETIMES: AspectLifetime[] = ["persistent", "scene", "exchange", "single-use"];
const SCOPES: AspectScope[] = ["table", "scene", "zone", "character", "npc"];

export default function AspectFormModal(props: AspectFormModalProps): ReactElement | null {
  const { t } = useTranslation();
  const kinds = props.allowedKinds ?? DEFAULT_KINDS;
  const [text, setText] = useState(props.initial?.text ?? "");
  const [kind, setKind] = useState<AspectKind>(props.initial?.kind ?? kinds[0] ?? "character");
  const [visibility, setVisibility] = useState<AspectVisibility>(
    props.initial?.visibility ?? "revealed",
  );
  const [lifetime, setLifetime] = useState<AspectLifetime>(props.initial?.lifetime ?? "persistent");
  const [scope, setScope] = useState<AspectScope>(props.initial?.scope ?? "character");

  if (!props.open) return null;

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    if (text.trim().length === 0) return;
    props.onSubmit({ text: text.trim(), kind, visibility, lifetime, scope });
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: native <dialog> requires imperative showModal(); ARIA dialog pattern is intentional
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="aspect-form-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md space-y-3 dialog-surface rounded-lg p-6 shadow-xl"
      >
        <h2 id="aspect-form-title" className="text-lg font-semibold">
          {props.mode === "create" ? t("edit.aspect.title.create") : t("edit.aspect.title.edit")}
        </h2>

        <label className="block text-sm">
          <span>{t("edit.aspect.text")}</span>
          <input
            className="mt-1 w-full rounded border px-2 py-1"
            value={text}
            onChange={(e) => setText(e.target.value)}
            required
            aria-required="true"
          />
        </label>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="block">
            <span>{t("edit.aspect.kind")}</span>
            <select
              className="mt-1 w-full rounded border px-2 py-1"
              value={kind}
              onChange={(e) => setKind(e.target.value as AspectKind)}
            >
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {t(`edit.aspect.kind.${k}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span>{t("edit.aspect.visibility")}</span>
            <select
              className="mt-1 w-full rounded border px-2 py-1"
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as AspectVisibility)}
            >
              <option value="revealed">{t("edit.aspect.visibility.revealed")}</option>
              <option value="hidden">{t("edit.aspect.visibility.hidden")}</option>
            </select>
          </label>

          <label className="block">
            <span>{t("edit.aspect.lifetime")}</span>
            <select
              className="mt-1 w-full rounded border px-2 py-1"
              value={lifetime}
              onChange={(e) => setLifetime(e.target.value as AspectLifetime)}
            >
              {LIFETIMES.map((l) => (
                <option key={l} value={l}>
                  {t(`edit.aspect.lifetime.${l}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span>{t("edit.aspect.scope")}</span>
            <select
              className="mt-1 w-full rounded border px-2 py-1"
              value={scope}
              onChange={(e) => setScope(e.target.value as AspectScope)}
            >
              {SCOPES.map((s) => (
                <option key={s} value={s}>
                  {t(`edit.aspect.scope.${s}`)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="rounded border px-3 py-1 text-sm"
            onClick={props.onCancel}
          >
            {t("edit.cancel")}
          </button>
          <button
            type="submit"
            className="rounded bg-emerald-600 px-3 py-1 text-sm text-white disabled:opacity-50"
            disabled={text.trim().length === 0}
          >
            {t("edit.save")}
          </button>
        </div>
      </form>
    </div>
  );
}
